import React from 'react';
import { useLocalization } from '@/Hooks';
import sharedStyles from '../ModalWindows.scss';
import { useSelector } from 'react-redux';
import { salesPageSelectors } from '@/Pages/Sales/services';
import { BUTTON_THEMES } from '@/Components/Controls/Button/ButtonThemes';
import { Button } from '@/Components/Controls/Button/Button';
import { Modal } from '@/Components/Modal/Modal';
import { ModalProps } from '@/Types';
import { useAllShippedForm } from './hooks/useAllShippedForm';
import { useShippedAndNotShippedForm } from './hooks/useShippedAndNotShippedForm';
import { useAllNotShippedForm } from './hooks/useAllNotShippedForm';
import styles from './ChangeDeliveryStatusModal.scss';
import { Label } from '@/Components/Controls/Label/Label';
import { FormDateInput } from '@/Components/Controls/DateInput/FormDateInput';
import { ChangeOrderDeliveryStatusByMarkerRequest } from '@/Api/MpOrdersMarking/Requests/ChangeOrderDeliveryStatusByMarkerRequest';
import { ChangeOrderDeliveryStatusPartiallyByMarkerRequest } from '@/Api/MpOrdersMarking/Requests/ChangeOrderDeliveryStatusPartiallyByMarkerRequest';
import { FormSelectInput } from '@/Components/Controls/SelectInput/FormSelectInput';
import { FormCheckbox } from '@/Components/Controls/Checkbox/FormCheckbox';
import { useShippedAndNotShippedFormOptions } from './hooks/useShippedAndNotShippedFormOptions';
import { useAllShippedBodyWarnings } from './hooks/useAllShippedBodyWarnings';
import { orderUtils } from '@/Utils/models/Order';
import { ButtonsWrapper } from '@/Components/Forms/ButtonsSection/ButtonsSection';

type Props = ModalProps;

type BodyProps = {
  onClose: () => void;
  orderIds: number[];
};

export function ChangeDeliveryStatusModal({ closeModalWindow }: Props) {
  const {
    modalWindows: { changeDeliveryStatus },
  } = useLocalization();

  const selectedOrders = useSelector(salesPageSelectors.selectedSales);
  const selectedOrderIds = useSelector(salesPageSelectors.selectedSalesIds);
  const isAllShipped = selectedOrders.every(orderUtils.isOrderShipped);
  const isAllNotShipped = selectedOrders.every((o) => !orderUtils.isOrderShipped(o));

  let Body: (props: BodyProps) => JSX.Element;

  if (isAllShipped) {
    Body = AllShippedBody;
  } else if (isAllNotShipped) {
    Body = AllNotShippedBody;
  } else {
    Body = ShippedAndNotShippedBody;
  }

  return (
    <Modal
      onClose={closeModalWindow}
      className={sharedStyles.modalWrapper}
      tourKey={'changeDeliveryStatusTour'}
      description={{
        title: changeDeliveryStatus.Title,
      }}
    >
      <div id={'changeStatus'}>
        <Body onClose={closeModalWindow} orderIds={selectedOrderIds} />
      </div>
    </Modal>
  );
}

function AllShippedBody({ onClose, orderIds }: BodyProps) {
  const {
    buttons,
    modalWindows: { changeDeliveryStatus },
  } = useLocalization();

  const { submit } = useAllShippedForm(orderIds, onClose);
  const { warnings } = useAllShippedBodyWarnings(orderIds);

  return (
    <>
      <p dangerouslySetInnerHTML={{ __html: changeDeliveryStatus.TextForAllShipped }} />

      {warnings.map((warning) => (
        <div className={styles.warningMessage} key={warning}>
          {warning}
        </div>
      ))}

      <ButtonsWrapper>
        <Button theme={BUTTON_THEMES.SUCCESS_TRANSPARENT} text={buttons.Cancel} onClick={onClose} />
        <Button autoFocus={true} theme={BUTTON_THEMES.SUCCESS} text={buttons.Change} onClick={submit} />
      </ButtonsWrapper>
    </>
  );
}

function AllNotShippedBody({ onClose, orderIds }: BodyProps) {
  type FormData = ChangeOrderDeliveryStatusByMarkerRequest;

  const {
    buttons,
    modalWindows: {
      changeDeliveryStatus: {
        TextForAllNotShipped,
        AllNotShippedForm: { ShippingDate },
      },
    },
  } = useLocalization();

  const {
    methods: { onSubmit, register, setValue },
    rules: { required },
    errors,
  } = useAllNotShippedForm(orderIds, onClose);

  return (
    <>
      <p dangerouslySetInnerHTML={{ __html: TextForAllNotShipped }} />

      <form className={styles.form}>
        <Label text={ShippingDate} className={styles.formLabel} />
        <FormDateInput<FormData>
          name={'shippingDate'}
          setValue={setValue}
          needTimePicker={true}
          register={register}
          rules={required}
          error={errors}
        />

        <ButtonsWrapper>
          <Button theme={BUTTON_THEMES.SUCCESS_TRANSPARENT} text={buttons.Cancel} onClick={onClose} tabIndex={-1} />
          <Button
            autoFocus={true}
            theme={BUTTON_THEMES.SUCCESS}
            text={buttons.Change}
            onClick={onSubmit}
            tabIndex={-1}
          />
        </ButtonsWrapper>
      </form>
    </>
  );
}

function ShippedAndNotShippedBody({ onClose, orderIds }: BodyProps) {
  type FormData = ChangeOrderDeliveryStatusPartiallyByMarkerRequest;

  const {
    buttons,
    modalWindows: {
      changeDeliveryStatus: {
        TextForShippedAndNotShipped,
        ShippedAndNotShippedForm: { SelectShippingStatus, OverwriteDates, ShippingDate },
      },
    },
  } = useLocalization();

  const {
    methods: { register, setValue, onSubmit },
    errors,
    needShowDate,
    rules: { dateRequired, required },
  } = useShippedAndNotShippedForm(orderIds, onClose);

  const { markerOptions } = useShippedAndNotShippedFormOptions();

  return (
    <>
      <p dangerouslySetInnerHTML={{ __html: TextForShippedAndNotShipped }} />

      <form className={styles.form}>
        <Label text={SelectShippingStatus} className={styles.formLabel} />
        <FormSelectInput<FormData>
          name={'orderMarker'}
          options={markerOptions}
          register={register}
          needClearButton={false}
          error={errors}
          rules={required}
        />
        {needShowDate && (
          <>
            <Label text={ShippingDate} className={styles.formLabel} />
            <FormDateInput<FormData>
              name={'shippingDate'}
              setValue={setValue}
              needTimePicker={true}
              register={register}
              rules={dateRequired}
              error={errors}
            />
            <Label text={OverwriteDates} className={styles.formLabel} />
            <FormCheckbox<FormData> register={register} setValue={setValue} name={'overwritePreviousDates'} />
          </>
        )}

        <ButtonsWrapper>
          <Button theme={BUTTON_THEMES.SUCCESS_TRANSPARENT} text={buttons.Cancel} onClick={onClose} />
          <Button autoFocus={true} theme={BUTTON_THEMES.SUCCESS} text={buttons.Update} onClick={onSubmit} />
        </ButtonsWrapper>
      </form>
    </>
  );
}
